"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { PieChart, Pie, Cell, Legend, ResponsiveContainer } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import axiosInstance from "@/api/axios"
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart"

type Season = "summer" | "winter" | "spring" | "autumn"

interface WasteCompositionProps {
  selectedSeason: Season
  onSeasonChange: (season: Season) => void
}

interface ApiWasteData {
  paper: number
  pmd: number
  residual: number
  organic: number
  total: number
  season?: string
}

interface ChartData {
  name: string
  value: number
  fill: string
}

const COLORS = {
  organic: "#2ECC71",
  paper: "#3498DB",
  pmd: "#F1C40F",
  residual: "#E67E22",
}

const emptyChartConfig: ChartConfig = {}

// Mock frequency data (since API doesn't provide frequency yet)
const mockFrequencyData = {
  summer: { organic: 80, paper: 60, pmd: 40, residual: 20 },
  winter: { organic: 70, paper: 50, pmd: 30, residual: 10 },
  spring: { organic: 75, paper: 55, pmd: 35, residual: 15 },
  autumn: { organic: 65, paper: 45, pmd: 25, residual: 10 },
}

const WasteComposition: React.FC<WasteCompositionProps> = ({ selectedSeason, onSeasonChange }) => {
  const [isFrequency, setIsFrequency] = useState(false)
  const [totalValue, setTotalValue] = useState<number>(0)
  const [wasteData, setWasteData] = useState<ApiWasteData | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const containerRef = useRef<HTMLDivElement>(null)

  const fetchWasteData = async (season: Season) => {
    setLoading(true)
    setError("")
    try {
      const params = { season }
      const response = await axiosInstance.get("others_page_waste_composition", { params })
      setWasteData(response.data)
    } catch (err: any) {
      console.error("Error fetching waste data:", err)
      setError(err.response?.data?.message || "Failed to fetch waste data")
      setWasteData({
        paper: 0,
        pmd: 0,
        residual: 0,
        organic: 0,
        total: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWasteData(selectedSeason)
  }, [selectedSeason])

  const transformToChartData = (): ChartData[] => {
    if (!wasteData) return []
    if (isFrequency) {
      const freqData = mockFrequencyData[selectedSeason]
      return [
        { name: "Organic", value: freqData.organic, fill: COLORS.organic },
        { name: "Paper", value: freqData.paper, fill: COLORS.paper },
        { name: "PMD", value: freqData.pmd, fill: COLORS.pmd },
        { name: "Residual", value: freqData.residual, fill: COLORS.residual },
      ]
    }
    return [
      { name: "Organic", value: wasteData.organic, fill: COLORS.organic },
      { name: "Paper", value: wasteData.paper, fill: COLORS.paper },
      { name: "PMD", value: wasteData.pmd, fill: COLORS.pmd },
      { name: "Residual", value: wasteData.residual, fill: COLORS.residual },
    ].filter((item) => item.value > 0)
  }

  const getData = () => {
    return transformToChartData()
  }

  useEffect(() => {
    const data = getData()
    const total = data.reduce((acc, curr) => acc + curr.value, 0)
    setTotalValue(total)
  }, [isFrequency, selectedSeason, wasteData])

  const displaySeasons = [
    { value: "summer", label: "Summer" },
    { value: "winter", label: "Winter" },
    { value: "spring", label: "Spring" },
    { value: "autumn", label: "Autumn" },
  ]

  const handleSeasonChange = (newSeason: string) => {
    const season = newSeason as Season
    onSeasonChange(season)
  }

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle className="text-base sm:text-lg text-center sm:text-left">Waste Composition</CardTitle>
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:gap-4">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm">KG</span>
              <Switch
                checked={isFrequency}
                onCheckedChange={() => setIsFrequency(!isFrequency)}
                className="bg-gray-200 dark:bg-gray-600"
              />
              <span className="text-sm">Freq</span>
            </div>
            <select
              className="p-2 rounded-md border bg-white text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[100px] text-sm"
              onChange={(e) => handleSeasonChange(e.target.value)}
              value={selectedSeason}
              disabled={loading}
            >
              {displaySeasons.map((season) => (
                <option key={season.value} value={season.value}>
                  {season.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pt-0 pb-3 sm:pb-6 flex-grow flex flex-col">
        {loading ? (
          <div className="flex-grow flex items-center justify-center">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : (
          <>
            <div className="flex-grow min-h-[200px] sm:min-h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <ChartContainer config={emptyChartConfig}>
                  <PieChart margin={{ top: 10, right: 5, bottom: 25, left: 5 }}>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Pie
                      data={getData()}
                      cx="50%"
                      cy="50%"
                      startAngle={180}
                      endAngle={0}
                      innerRadius="45%"
                      outerRadius="75%"
                      dataKey="value"
                      nameKey="name"
                      label={(entry) => {
                        if (entry.value === 0) return ""
                        return `${entry.value}${isFrequency ? "" : " kg"}`
                      }}
                      labelLine={{ stroke: "#666", strokeWidth: 1 }}
                    >
                      {getData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={25}
                      layout="horizontal"
                      align="center"
                      wrapperStyle={{
                        paddingTop: 5,
                        fontSize: "12px",
                      }}
                    />
                  </PieChart>
                </ChartContainer>
              </ResponsiveContainer>
            </div>
            <div className="text-center text-sm sm:text-base font-semibold mt-2">
              <div>
                Total: {isFrequency ? totalValue.toFixed(2) : wasteData?.total.toFixed(2) || 0} {isFrequency ? "" : "KG"}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default WasteComposition
